// "Mente du …@gmail.com?" under the wizard's e-mail field. Only offered for a
// near miss of a mail provider our customers use; a company's own domain is
// never second-guessed. Real domains that sit close to a big one (godmail.dk vs
// hotmail.dk) are listed so they are never "corrected".
const KNOWN_DOMAINS = [
  "gmail.com",
  "hotmail.com",
  "outlook.dk",
  "live.dk",
  "hotmail.dk",
  "icloud.com",
  "yahoo.dk",
  "mail.dk",
  "yahoo.com",
  "outlook.com",
  "live.com",
  "msn.com",
  "me.com",
  "mac.com",
  "googlemail.com",
  "jubii.dk",
  "sol.dk",
  "youmail.dk",
  "godmail.dk",
  "dukamail.dk",
  "dadlnet.dk",
  "onlinepost.dk",
  "fiberpost.dk",
  "stofanet.dk",
  "comxnet.dk",
  "mail.tele.dk",
  "post.tele.dk",
  "protonmail.com",
  "proton.me",
  "passmail.com",
];

export function suggestEmail(email: string): string | null {
  const trimmed = email.trim();
  const at = trimmed.lastIndexOf("@");
  if (at < 1) return null;
  const domain = trimmed.slice(at + 1).toLowerCase();
  if (!domain.includes(".") || KNOWN_DOMAINS.includes(domain)) return null;
  const suggestion = sameProviderOtherTld(domain) ?? closestKnownDomain(domain);
  return suggestion ? `${trimmed.slice(0, at)}@${suggestion}` : null;
}

// "gmail.dk" is gmail.com with the wrong ending, not mail.dk with an extra g.
function sameProviderOtherTld(domain: string): string | null {
  const name = withoutTld(domain);
  const [only, ...others] = KNOWN_DOMAINS.filter((known) => withoutTld(known) === name);
  return only && others.length === 0 ? only : null;
}

function closestKnownDomain(domain: string): string | null {
  const allowed = domain.length < 10 ? 1 : 2;
  let best: { domain: string; distance: number } | null = null;
  for (const known of KNOWN_DOMAINS) {
    const distance = editDistance(domain, known);
    if (distance <= allowed && (!best || distance < best.distance))
      best = { domain: known, distance };
  }
  return best?.domain ?? null;
}

function withoutTld(domain: string): string {
  return domain.slice(0, domain.lastIndexOf("."));
}

// Optimal string alignment distance: a swapped pair ("gmial") counts as one
// edit, which is why it keeps the row before the previous one.
function editDistance(a: string, b: string): number {
  let twoBack: number[] = [];
  let previous = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const current = [i];
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let distance = Math.min(
        (previous[j] ?? 0) + 1,
        (current[j - 1] ?? 0) + 1,
        (previous[j - 1] ?? 0) + cost,
      );
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        distance = Math.min(distance, (twoBack[j - 2] ?? 0) + 1);
      }
      current.push(distance);
    }
    twoBack = previous;
    previous = current;
  }
  return previous[b.length] ?? 0;
}
